import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { rpc } from "../../lib/rpc";
import { AdminPageHeader } from "../../components/admin/DataTable";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";

function Card({ title, value, tone, hint, to }) {
    const cls = tone === "warning" ? "text-warning" : tone === "destructive" ? "text-destructive" : "text-secondary";
    return (
        <Link
            to={to || "#"}
            className="rounded-xl border border-border bg-card/60 p-4 transition-colors hover:border-primary/50"
            data-testid={`dq-card-${title.replace(/\s+/g, "-").toLowerCase()}`}
        >
            <div className="text-xs uppercase tracking-[0.2em] text-muted-foreground">{title}</div>
            <div className={`mt-1 text-3xl font-black ${cls}`}>{value}</div>
            {hint ? <p className="mt-1 text-xs text-muted-foreground">{hint}</p> : null}
        </Link>
    );
}

const CLAIM_FLOW_LABELS = {
    pick_list_complete: "Picklisten-Abschluss",
    inventory_apply: "Inventur-Anwendung",
};

function describeStep(step) {
    const article = step.article_name || step.article_id || "unbekannter Artikel";
    const pos = step.position_label || step.stock_position_id || "unbekannte Position";
    const span = step.previous_quantity != null && step.new_quantity != null
        ? ` (${step.previous_quantity} → ${step.new_quantity})`
        : "";
    if (step.kind === "reservation_released") {
        return `Reservierung freigegeben: ${step.quantity} × ${article} in ${pos}`;
    }
    if (step.kind === "stock_deducted") {
        return `Bestand gebucht: ${step.quantity} × ${article} aus ${pos}${span}`;
    }
    if (step.kind === "quantity_set") {
        return `Bestand gesetzt: ${article} in ${pos}${span}`;
    }
    return `${step.kind}: ${article} in ${pos}${span}`;
}

// #73: der Admin entscheidet ueber Verwerfen anhand der bereits gebuchten
// Effekte -- nicht anhand des rohen Movement-Dokuments.
function PendingClaimRow({ claim, onDiscarded }) {
    const [reason, setReason] = useState("");
    const [error, setError] = useState(null);
    const [busy, setBusy] = useState(false);

    const progress = claim.progress || null;
    const applied = progress?.applied || [];
    const tooYoung = (claim.age_minutes ?? 0) < 15;
    const flowLabel =
        CLAIM_FLOW_LABELS[progress?.flow] || claim.type || "Vorgang";

    async function discard() {
        setBusy(true);
        setError(null);
        const r = await rpc.post(`/movements/pending-claims/${claim.id}/discard`, { reason });
        setBusy(false);
        if (r.ok) onDiscarded(claim.id);
        else setError(r.error);
    }

    return (
        <div
            className="rounded-xl border border-border bg-card/60 p-4"
            data-testid={`dq-claim-${claim.id}`}
        >
            <div className="flex flex-wrap items-baseline gap-x-3 gap-y-1">
                <span className="text-sm font-semibold">{flowLabel}</span>
                {claim.article_name ? (
                    <span className="text-xs text-muted-foreground">{claim.article_name}</span>
                ) : null}
                <span className="text-xs text-muted-foreground">
                    seit {Math.round(claim.age_minutes ?? 0)} min offen
                </span>
            </div>

            {claim.effect_started === false ? (
                <p className="mt-2 text-xs text-secondary" data-testid="claim-no-effect">
                    Noch kein Bestandseffekt gebucht — Verwerfen ist gefahrlos.
                </p>
            ) : claim.effect_started === true ? (
                <div className="mt-2" data-testid="claim-partial-effect">
                    <p className="text-xs font-medium text-destructive">
                        Effekte wurden bereits gebucht ({applied.length}
                        {progress?.total_steps ? ` von ${progress.total_steps}` : ""} Schritten) —
                        vor dem Verwerfen Bestand prüfen:
                    </p>
                    <ul className="mt-1 list-disc space-y-0.5 pl-5 text-xs">
                        {applied.map((s, i) => (
                            <li key={i}>{describeStep(s)}</li>
                        ))}
                        {progress?.in_flight ? (
                            <li className="text-warning">
                                Unbestimmt (lief beim Abbruch): {describeStep(progress.in_flight)}
                            </li>
                        ) : null}
                    </ul>
                </div>
            ) : (
                <p className="mt-2 text-xs text-muted-foreground" data-testid="claim-unknown-effect">
                    Kein Fortschrittsprotokoll (älterer Vorgang) — Bestand und Verlauf manuell prüfen.
                </p>
            )}

            <div className="mt-3 flex flex-wrap items-center gap-2">
                <Input
                    value={reason}
                    onChange={(e) => setReason(e.target.value)}
                    placeholder="Grund für das Verwerfen (Pflicht)"
                    aria-label="Grund für das Verwerfen"
                    className="h-8 max-w-xs text-xs"
                    data-testid="claim-discard-reason"
                />
                <Button
                    variant="outline"
                    size="sm"
                    disabled={busy || tooYoung || reason.trim().length < 5}
                    onClick={discard}
                    data-testid="claim-discard"
                >
                    Verwerfen
                </Button>
                {tooYoung ? (
                    <span className="text-xs text-muted-foreground">
                        Erst ab 15 min möglich — der Vorgang könnte noch laufen.
                    </span>
                ) : null}
                {error ? <span className="text-xs text-destructive">{error}</span> : null}
            </div>
        </div>
    );
}

export default function AdminDatenqualitaet() {
    const [articles, setArticles] = useState([]);
    const [positions, setPositions] = useState([]);
    const [kpis, setKpis] = useState(null);
    const [comps, setComps] = useState([]);
    const [restock, setRestock] = useState([]);
    const [pendingClaims, setPendingClaims] = useState([]);
    const [settings, setSettings] = useState(null);

    // #136 Befund 6: nach In-Page-Aktionen (z. B. Claim verwerfen) alles neu
    // laden, damit Karten und Zähler ohne Browser-Reload stimmen.
    const load = () =>
        Promise.all([
            rpc.get("/master/articles"),
            rpc.get("/master/stock-positions"),
            rpc.get("/master/compartments"),
            rpc.get("/restock"),
            rpc.get("/dashboard/kpis"),
            rpc.get("/movements/pending-claims"),
            rpc.get("/settings"),
        ]).then(([a, p, c, r, k, pc, st]) => {
            if (a.ok) setArticles(a.data);
            if (p.ok) setPositions(p.data);
            if (c.ok) setComps(c.data);
            if (r.ok) setRestock(r.data);
            if (k.ok) setKpis(k.data);
            if (pc.ok) setPendingClaims(pc.data);
            if (st.ok) setSettings(st.data);
        });
    useEffect(() => { load(); }, []);

    const now = Date.now();
    // Schwellen aus Admin → Einstellungen (#125); Defaults bis geladen.
    const restockDays = settings?.restock_stale_days ?? 14;
    const stagingDays = settings?.staging_stale_days ?? 7;
    const d14 = restockDays * 86400000;
    const d7 = stagingDays * 86400000;

    const artikelOhneMin = articles.filter((a) => a.min_quantity == null && a.lifecycle === "active").length;
    const artikelOhneKat = articles.filter((a) => !a.category_id && a.lifecycle === "active").length;
    const stagingAlt = positions.filter((p) => {
        const c = comps.find((x) => x.id === p.compartment_id);
        return c?.is_staging && (p.quantity || 0) > 0 && new Date(p.created_at).getTime() < now - d7;
    }).length;
    const pendingOldRestock = restock.filter(
        (r) => r.status === "pending" && new Date(r.created_at).getTime() < now - d14
    ).length;
    // F6: Claims, die einen Crash/Deploy zwischen claim und finalize
    // ueberlebt haben -- Recovery siehe Handbuch 10.8 / API pending-claims.
    const staleClaims = pendingClaims.filter((m) => (m.age_minutes ?? 0) > 15).length;

    return (
        <>
            <AdminPageHeader title="Datenqualität" subtitle="Tote-Winkel-Dashboard" />
            <div className="grid grid-cols-2 gap-4 lg:grid-cols-4" data-testid="dq-grid">
                <Card title="Artikel ohne Min" value={artikelOhneMin} tone="warning" to="/admin/artikel?dq=ohne-min" />
                <Card title="Artikel ohne Kategorie" value={artikelOhneKat} tone="warning" to="/admin/artikel?dq=ohne-kategorie" />
                <Card title={`Staging > ${stagingDays} Tage`} value={stagingAlt} tone="destructive" to="/admin/lagerstruktur" />
                <Card title={`Pending Restock > ${restockDays} Tage`} value={pendingOldRestock} tone="destructive" to="/admin/nachbestellung" />
                <Card title="Überfällige Ausleihen" value={kpis?.overdue_loans ?? 0} tone="destructive" to="/admin/ausleihen" />
                <Card title="Kritische Artikel" value={kpis?.critical_stock ?? 0} tone="destructive" to="/admin/artikel" />
                <Card title="Niedriger Bestand" value={kpis?.low_stock ?? 0} tone="warning" to="/admin/artikel" />
                <Card
                    title="Hängende Buchungen"
                    value={staleClaims}
                    tone="destructive"
                    to="/admin/datenqualitaet"
                    hint="pending > 15 min — Details unten"
                />
            </div>

            {pendingClaims.length > 0 ? (
                <section className="mt-6" data-testid="dq-pending-claims">
                    <h2 className="mb-2 text-xs uppercase tracking-[0.2em] text-muted-foreground">
                        Hängende Buchungen
                    </h2>
                    <div className="space-y-3">
                        {pendingClaims.map((claim) => (
                            <PendingClaimRow
                                key={claim.id}
                                claim={claim}
                                onDiscarded={(id) => {
                                    setPendingClaims((list) => list.filter((c) => c.id !== id));
                                    load();
                                }}
                            />
                        ))}
                    </div>
                </section>
            ) : null}
        </>
    );
}
